import React from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { THYROID_TESTS, BLOOD_PANEL_TESTS } from "./constants";
import { CLINIC_COLORS } from "./theme";

const numericKeyDown = (e) => {
  const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End", ".", ","];
  if (!allowedKeys.includes(e.key) && !/[0-9]/.test(e.key)) {
    e.preventDefault();
  }
};

const inputStyle = {
  background: "#f1f4f9",
  border: "2px solid transparent",
  transition: "border-color 0.2s, background-color 0.2s",
};
const onInputFocus = (e) => {
  e.target.style.borderColor = CLINIC_COLORS.primary;
  e.target.style.background = "#ffffff";
};
const onInputBlur = (e) => {
  e.target.style.borderColor = "transparent";
  e.target.style.background = "#f1f4f9";
};

function TestRow({ test, value, onChange, i18nKey, placeholder }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4 mb-4">
      <div className="w-28 flex items-center gap-2">
        <Label htmlFor={test.key} className="font-semibold" style={{ color: CLINIC_COLORS.muted }}>
          {i18nKey ? t(i18nKey) : t(`tests.${test.key}`)}
        </Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" style={{ color: CLINIC_COLORS.placeholder }}>
              <Info className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-sm">
            <p className="text-sm">{test.description}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex-1 relative">
        <Input
          id={test.key}
          data-testid={`input-${test.key}`}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          placeholder={placeholder}
          className="pr-20 h-12 text-base"
          style={inputStyle}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          value={value || ""}
          onChange={(e) => onChange(test.key, e.target.value)}
          onKeyDown={numericKeyDown}
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: CLINIC_COLORS.subtle }}>
          {test.unit}
        </span>
      </div>
    </div>
  );
}

export const LabResultsSection = ({ testValues, handleInputChange, isBloodPanelRequired, error }) => {
  const { t } = useTranslation();

  return (
    <>
      {error && (
        <div
          className="text-sm p-4 rounded-xl mb-6"
          style={{ background: CLINIC_COLORS.warmAmberBg, color: CLINIC_COLORS.warmAmber }}
        >
          {error}
        </div>
      )}

      <div className="space-y-5">
        {THYROID_TESTS.map((test, index) => (
          <div
            key={test.key}
            className={`animate-fade-in opacity-0 stagger-${index + 1}`}
            style={{ animationFillMode: "forwards" }}
          >
            <TestRow
              test={test}
              value={testValues[test.key]}
              onChange={handleInputChange}
              placeholder={test.key === "tsh" ? "e.g., 0.005" : ""}
            />
          </div>
        ))}

        {/* Blood Panel Section */}
        {isBloodPanelRequired && (
          <div className="mt-8 pt-6">
            <div className="mb-4">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: CLINIC_COLORS.ink }}>
                <span className="w-2 h-2 rounded-full" style={{ background: CLINIC_COLORS.warmAmber }}></span>
                {t("dashboard.bloodPanel")}
              </h3>
              <p className="text-sm mt-1" style={{ color: CLINIC_COLORS.subtle }}>
                {t("dashboard.bloodPanelRequired")}
              </p>
            </div>

            {BLOOD_PANEL_TESTS.map((test) => (
              <TestRow
                key={test.key}
                test={test}
                value={testValues[test.key]}
                onChange={handleInputChange}
                i18nKey={`dashboard.${test.key}`}
                placeholder={t("dashboard.required")}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
